import {
  createRootRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { GraduationCap, LogOut, Menu, PanelLeft } from "lucide-react"
import * as React from "react"

import { NotFound } from "@/components/not-found"
import { ThemeToggle } from "@/components/theme-toggle"
import { BAND_SCALE, bandStyle, Button } from "@/components/ui"
import { FeedbackButton } from "@/features/feedback/feedback-button"
import { AccountButton } from "@/features/users/account-button"
import { INSTANCE } from "@/lib/instance"
import { visibleNavGroups } from "@/lib/nav"
import { useLogout, useSession } from "@/lib/session"
import { recordVisit } from "@/lib/trail"
import { cn } from "@/lib/utils"

// The navigation model and its Administrator gating live in `lib\nav.ts`: a
// TanStack route file may export nothing but its `Route`, so anything in one
// is untestable by construction, and which screens a Coordinator is offered is
// a rule worth a test.

export const Route = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
})

/**
 * The S that joins the footer line's low run to the plate the wordmark sits
 * on: the page band's own joint, rotated into the bottom-right corner.
 *
 * Same numbers as `ScurveJoint` in `components\ui.tsx`, taken from the same
 * `BAND_SCALE`, so the two curves are one shape and cannot drift; only the
 * path is turned over. The rim line sits at `box - rim` here rather than at
 * `rim`, which is the same thickness measured from the other edge.
 *
 * Three things make the hairline continuous rather than nearly so, and all
 * three were visible defects before they were done:
 *
 *   - the line is an OPEN stroked path, not a stroke on the filled silhouette,
 *     which would draw the shape's left edge too and show as a vertical seam
 *   - the stroke runs half a unit BELOW the fill's own edge, because a 1px
 *     border occupies the pixel under the boundary while a 1px stroke straddles
 *     it; half a unit down is what puts the two on the same pixel row
 *   - `-mx-px` overlaps the low run on one side and lets the plate overlap this
 *     drawing on the other, so no background can show through a rounding gap
 *
 * The fill is white literally rather than a theme token, because it is the
 * plate the logo sits on and the CI's logo rules fix that plate to white in
 * any theme; the hairline takes the border token so it stays visible on both
 * canvases.
 */
function FooterSweep() {
  const { rim, box, sweep } = BAND_SCALE.page
  const low = box - rim
  const half = sweep / 2
  return (
    <svg
      viewBox={`0 0 ${sweep} ${box}`}
      preserveAspectRatio="none"
      className="-mx-px h-[var(--band-h)] w-[var(--band-sweep)] shrink-0 text-border-strong"
      aria-hidden
    >
      <path fill="#ffffff" d={`M0 ${box} V${low} C${half} ${low} ${half} 0 ${sweep} 0 V${box} Z`} />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
        d={`M0 ${low + 0.5} C${half} ${low + 0.5} ${half} 0.5 ${sweep} 0.5`}
      />
    </svg>
  )
}

/**
 * The wordmark and its suffix, locked to one width.
 *
 * Dawid's rule (2026-08-01): RAINBOW and TRAINING MANAGEMENT are exactly the
 * same width, and the suffix line is what decides it. `width: 0` stops the
 * image's intrinsic width driving the box, `min-width: 100%` then stretches it
 * to the container, and the container's width comes from the nowrap suffix
 * alone. The wordmark is the client's own extracted artwork, never recreated:
 * the CI's Logo Usage page forbids changing the mark or its font.
 *
 * The suffix's `em` values resolve against its own font size, which is how
 * 0.32em becomes the ~3px gap the mock renders rather than a third of the
 * banner. Its red is the literal fill red, not a token: this plate is white in
 * both themes, and 9px letters need 4.5:1 on white, which the CI hex (4.12:1)
 * does not give.
 *
 * 0.366em is not taste: it is what makes the wordmark's cap height equal the
 * band title's cap height at ANY base size, which is Dawid's 2026-08-02
 * ruling (title the same size as the logo). Measured in the rendered app by
 * `tools\measure-band-lockup.mjs`: the title carries 0.714 caps per px of
 * font size, and at the previous 0.28em the wordmark carried 0.546, so the
 * lockup grows by their ratio. Both sides of the equation scale with the
 * same base, so the equality survives any future re-size of the band.
 */
function Lockup() {
  return (
    <div className="inline-block">
      <img
        src={INSTANCE.logo}
        alt={INSTANCE.logoAlt}
        className="block h-auto w-0 min-w-full"
      />
      <span className="mt-[0.32em] block whitespace-nowrap font-display text-[0.366em] font-bold uppercase leading-none tracking-[0.16em] text-[#d7261a]">
        {INSTANCE.tagline}
      </span>
    </div>
  )
}

const COLLAPSE_KEY = "rc-tms-nav-collapsed"

function RootLayout() {
  const { pathname } = useRouterState({ select: (state) => state.location })
  const navigate = useNavigate()
  const { me, isLoading, isError, isAdministrator, isSuperuser } = useSession()
  const logout = useLogout()
  const [navOpen, setNavOpen] = React.useState(false)
  // Desktop only: the sidebar narrows to an icon rail and remembers it, per
  // the design reference. On a phone the sidebar is an overlay instead, and
  // an icon rail on an overlay would be the worst of both.
  const [collapsed, setCollapsed] = React.useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1"
    } catch {
      return false
    }
  })

  function toggleCollapsed() {
    setCollapsed((current) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, current ? "0" : "1")
      } catch {
        // Unstorable is not unusable: it just will not survive a reload.
      }
      return !current
    })
  }

  const onLoginScreen = pathname === "/login"

  // A 401 means nobody is signed in. Send them to login rather than showing an
  // error: the design contract is explicit that 401 is not a toast.
  React.useEffect(() => {
    if (!isLoading && !me && !onLoginScreen) {
      void navigate({ to: "/login", replace: true })
    }
  }, [isLoading, me, onLoginScreen, navigate, isError])

  /**
   * Where focus goes when the route changes.
   *
   * A single-page application replaces the page's contents without the browser
   * ever performing a navigation, so nothing resets focus: it stays on the nav
   * link that was clicked, and a screen reader announces nothing at all,
   * because from its point of view nothing happened. The user is then reading a
   * new screen while their cursor is still in the navigation of the old one.
   *
   * Moving focus to `main` is what a real page load would have done. `main`
   * carries `tabIndex={-1}` so it can receive focus programmatically without
   * ever becoming a tab stop of its own.
   *
   * The first render is deliberately skipped. On arrival there has been no
   * navigation to announce, and stealing focus from wherever the browser put it
   * (an autofocused field, a restored scroll position) is a change nobody asked
   * for.
   */
  /**
   * The session's own trail, fed from the router and read only if somebody
   * gives feedback (J5).
   *
   * Wired here because this is the one component every navigation passes
   * through, and wired to the pathname alone: a search string on these screens
   * carries what somebody typed into a search box, which on the employee list
   * is a colleague's surname, and the trail's job is to name the screen rather
   * than to copy business data into a second place. Nothing is persisted; the
   * buffer dies with the tab.
   *
   * Not skipped on first render, unlike the focus effect below: the screen a
   * session started on is a step of the trail, and the effect only appends.
   */
  React.useEffect(() => {
    recordVisit(pathname)
  }, [pathname])

  const mainRef = React.useRef<HTMLElement>(null)
  const navigated = React.useRef(false)
  React.useEffect(() => {
    if (!navigated.current) {
      navigated.current = true
      return
    }
    mainRef.current?.focus()
  }, [pathname])

  if (onLoginScreen) return <Outlet />

  if (isLoading || !me) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background">
        <span className="sr-only">Loading</span>
      </div>
    )
  }

  const groups = visibleNavGroups({ isAdministrator, isSuperuser })

  return (
    <div className="flex min-h-dvh bg-background">
      {/* THE FIRST THING IN THE DOCUMENT, and seen only by someone who tabs.
          Measured 2026-08-04: fifteen Tab presses separate the top of the
          document from the first control in the page body - nine nav links,
          the rail collapse, three theme buttons and sign out - and they were
          re-traversed on every single navigation. This is the one press that
          replaces them. Styling is the `skip-link` utility in `styles.css`,
          which clips it to nothing while keeping it in the tab order, because
          `display: none` would take it out of the one place it has to be. */}
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {/* Full height and its own scroll, so the navigation does not run out
          part-way down a long table the way a document-flow column does. */}
      <nav
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-64 shrink-0 flex-col overflow-y-auto border-r border-sidebar-border bg-sidebar px-3 py-4 transition-[width] duration-200 lg:static lg:flex",
          collapsed && "lg:w-[4.25rem] lg:px-2",
          navOpen ? "flex" : "hidden",
        )}
        aria-label="Main"
      >
        <div
          className={cn(
            "mb-5 flex items-center gap-2.5 border-b border-sidebar-border px-2 pb-4",
            collapsed && "lg:justify-center lg:px-0",
          )}
        >
          {/* White mark on the CI red, the same pairing as the GUS badge. */}
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-brand text-brand-foreground shadow-soft">
            <GraduationCap className="h-4 w-4" aria-hidden />
          </div>
          {/* The name comes from the instance slot in `lib\instance.ts`,
              never from a screen: branding is instance configuration. */}
          <div className={cn("min-w-0", collapsed && "lg:hidden")}>
            <div className="truncate font-display text-sm font-bold text-sidebar-foreground">
              {INSTANCE.name}
            </div>
            <div className="truncate text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {INSTANCE.tagline}
            </div>
          </div>
        </div>

        {groups.map((group, index) => (
          <div key={group.label ?? index} className="mb-4">
            {group.label && (
              <p
                className={cn(
                  "mb-1.5 px-3 text-[0.625rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground/70",
                  collapsed && "lg:hidden",
                )}
              >
                {group.label}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  {/* The active item wears three signals at once: a tinted
                      surface, a short brand bar on its left edge, and the
                      icon in the brand colour. Any one alone is easy to
                      miss against a quiet sidebar. */}
                  <Link
                    to={to}
                    onClick={() => setNavOpen(false)}
                    title={collapsed ? label : undefined}
                    className={cn(
                      "group relative flex h-10 items-center gap-2.5 overflow-hidden rounded-xl px-3 text-[13px] font-medium text-sidebar-foreground transition-all duration-200 hover:bg-sidebar-accent",
                      collapsed && "lg:justify-center lg:px-0",
                    )}
                    activeProps={{
                      className:
                        "bg-primary/[0.07] text-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-primary before:content-[''] [&_svg]:text-primary",
                      // The three visual signals above say "you are here" to
                      // anyone who can see them, and said it to nobody else:
                      // `aria-current="page"` is the same statement in the
                      // accessibility tree, and it is what a screen reader
                      // announces when the user walks the navigation.
                      "aria-current": "page",
                    }}
                    activeOptions={{ exact: to === "/" }}
                  >
                    <Icon
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-colors"
                      aria-hidden
                    />
                    <span className={cn(collapsed && "lg:hidden")}>{label}</span>
                    {/* The icon rail strips the label to `display: none` and
                        the icon is `aria-hidden`, which left the link with no
                        accessible name at all: nine links announced as "link"
                        and nothing else. `title` is not a name a screen reader
                        can be relied on to read, and it is unreachable by
                        keyboard or touch anyway, so the name is restored in
                        text. Rendered only when collapsed, and only at `lg`,
                        which is exactly where the visible label is not. */}
                    {collapsed && (
                      <span className="sr-only hidden lg:inline">{label}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* True of the product as built: the M4 machine credential reads
            these records over employee number, which is the LMS's join key.
            Pinned to the bottom, and gone when the rail is icons-only. */}
        <div
          className={cn(
            "mt-auto rounded-xl border border-sidebar-border bg-surface/60 p-3 text-xs text-muted-foreground",
            collapsed && "lg:hidden",
          )}
        >
          <div className="brand-rule mb-2" aria-hidden />
          <div className="font-medium text-foreground">Records your LMS can read</div>
          <p className="mt-1 leading-relaxed">
            Every training record carries its employee number and its training
            event, so your learning system can match them up.
          </p>
        </div>
      </nav>

      {navOpen && (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur">
          <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setNavOpen((open) => !open)}
              aria-label="Toggle navigation"
              aria-expanded={navOpen}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              aria-expanded={!collapsed}
            >
              <PanelLeft className="h-5 w-5" aria-hidden />
            </Button>
            <div className="ml-auto flex items-center gap-2">
              {/* Who is signed in, and the way in to Your account (item 38).
                  It was a plain label until then, and the label was already
                  the one thing on every screen that says "this is you", so it
                  became the control rather than growing a second one beside
                  it. The identity line is still composed in `lib\session.ts`
                  (R8: the role reads in this instance's own vocabulary). Every
                  role gets it: the dialog behind it changes nothing but your
                  own username and password. */}
              <AccountButton me={me} />
              {/* Every signed-in person, Viewers included (J4), on every
                  screen and at every width. It sits in the top bar rather than
                  the sidebar footer because the rail collapses that footer
                  away and a phone hides the whole sidebar behind a press,
                  which is one press too many for somebody who is stuck. */}
              <FeedbackButton route={pathname} />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                loading={logout.isPending}
                onClick={() =>
                  logout.mutate(undefined, {
                    onSuccess: () => void navigate({ to: "/login", replace: true }),
                  })
                }
              >
                <LogOut className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Sign out</span>
                {/* Below `sm` the label above is `display: none` and the icon
                    is `aria-hidden`, so the button announced itself as nothing
                    at all - on a phone, for the one control that ends the
                    session. The two spans are mutually exclusive at every
                    width, so the name is "Sign out" and never "Sign out Sign
                    out". Copy, not an aria-label: the same words the button
                    shows on a wider screen. */}
                <span className="sr-only sm:hidden">Sign out</span>
              </Button>
            </div>
          </div>
        </header>

        {/* `tabIndex={-1}` is what lets the skip link above and the route
            effect focus this element: without it a `<main>` is not focusable
            and `#main` moves the scroll position while leaving the keyboard
            exactly where it was, which is the failure that makes skip links
            look as though they do nothing. -1 rather than 0, so it is
            focusable programmatically and never a tab stop in its own right.
            `outline-none` is deliberately NOT set here: a programmatic focus
            on a container does not match `:focus-visible`, so no ring is drawn,
            and cancelling it would break the one case where it should be. */}
        <main id="main" ref={mainRef} tabIndex={-1} className="min-w-0 flex-1">
          {/* Capped, because a table stretched across an ultrawide monitor is
              a table nobody can read a row of. */}
          <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
            <Outlet />
          </div>
        </main>

        {/* The page footer: the header's own corner block, rotated into the
            bottom-right corner (Dawid's ruling, 2026-08-01, footer treatment
            A of the mock of record). A hairline runs low across the width,
            steps up through the S, and continues straight across the plate's
            top OVER the wordmark to the page edge: one unbroken boundary, not
            three pieces that happen to meet.

            Three elements rather than one stretched drawing, so that only the
            low run flexes with the window while the S keeps its aspect.

            The em base is the page header's title size, so the plate and the
            band are the same box: 1.6 x the page title. Both are held to `main`'s own
            column, capped and padded the same way, so the lockup's right edge
            lands on the content column's right edge exactly as the title's
            left edge lands on its left.

            The white stays white in both themes, because the CI puts the logo
            on white or flat brand colours and nothing else. */}
        <footer className="mt-auto" aria-label="Site footer" style={bandStyle("page")}>
          <div className="mx-auto flex w-full max-w-[1600px] items-end [--column-inset:1rem] sm:[--column-inset:1.5rem] lg:[--column-inset:2rem]">
            <div className="h-[var(--band-rim)] flex-1 border-t border-border-strong bg-white" />
            <FooterSweep />
            <div className="flex h-[var(--band-h)] shrink-0 items-center border-t border-border-strong bg-white py-[var(--band-rim)] pr-[var(--column-inset)]">
              <Lockup />
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
